import { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { AvatarImage } from './AvatarImage';
import './featuredProfiles.css';
import { FeaturedProfile } from '../../utils/types';
import { useSelector } from 'react-redux';
import { getFeaturedProfiles } from '../../api/profile';
import { syncViewedProfiles } from '../../api/profileView';
import { getUserDisplayName } from '@/utils/userDisplay';
import {
    addMultipleViewedProfileIds,
    shouldSyncWithServer
} from '../../utils/storage';
import { Star } from 'lucide-react';
import {
    FEATURED_AVATAR_PX,
    FEATURED_ROW_PX,
    MIN_FEATURED_SLOTS,
    featuredAvatarPx,
    featuredSlotCount,
    mergeFeaturedProfiles,
    shouldFillFeaturedRows,
    visibleFeaturedCount,
} from './featuredSlots';

const MEASURE_DEBOUNCE_MS = 100;

const featuredRowClassName = 'flex shrink-0 items-center border-b border-gray-200 px-3 last:border-b-0';
const featuredRowStyle = { height: FEATURED_ROW_PX };
const fillRowClassName = 'flex min-h-0 flex-1 items-center overflow-hidden border-b border-gray-200 px-3 last:border-b-0';

interface FeaturedProfilesProps {
    /** Fixed keeps three natural rows. Fluid grows past the cap and drops to fewer rows when the pane is short. */
    mode?: 'fixed' | 'fluid';
}

const FeaturedProfiles = ({ mode = 'fluid' }: FeaturedProfilesProps) => {
    const user = useSelector((state: any) => state.auth.user);
    const [featuredProfiles, setFeaturedProfiles] = useState<FeaturedProfile[] | null>(null);
    const [slotCount, setSlotCount] = useState<number | null>(null);
    const [listHeight, setListHeight] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const profilesRef = useRef<FeaturedProfile[] | null>(null);

    const userId = useMemo(() => user?._id, [user?._id]);

    // The list area is the space under the header. Its height decides the request
    // size, so a 4K sidebar fills with more rows and a short one drops toward one.
    useLayoutEffect(() => {
        const list = listRef.current;
        if (!list) return undefined;

        if (typeof ResizeObserver === 'undefined') {
            setSlotCount(MIN_FEATURED_SLOTS);
            return undefined;
        }

        let flushTimer = 0;
        let slot: number | null = null;

        // Row count updates immediately. Sub-row height changes wait, so the ticker
        // band's short height animation does not re-render this list on every frame.
        const publish = () => {
            const height = list.clientHeight;
            const next = height <= 0
                ? null
                : mode === 'fixed'
                    ? MIN_FEATURED_SLOTS
                    : featuredSlotCount(height);

            if (next !== slot) {
                slot = next;
                window.clearTimeout(flushTimer);
                setListHeight((prev) => (prev === height ? prev : height));
                setSlotCount((prev) => (prev === next ? prev : next));
                return;
            }

            window.clearTimeout(flushTimer);
            flushTimer = window.setTimeout(() => {
                setListHeight((prev) => (Math.abs(prev - height) < 1 ? prev : height));
            }, 140);
        };

        publish();
        const observer = new ResizeObserver(publish);
        observer.observe(list);
        return () => {
            window.clearTimeout(flushTimer);
            observer.disconnect();
        };
    }, [mode]);

    useEffect(() => {
        profilesRef.current = null;
        setFeaturedProfiles(null);
        setError(null);
        setIsLoading(true);
    }, [userId]);

    useEffect(() => {
        if (!userId || slotCount == null) return undefined;

        let cancelled = false;
        const timer = window.setTimeout(() => {
            const current = profilesRef.current ?? [];
            if (current.length >= slotCount) {
                setIsLoading(false);
                return;
            }

            const need = slotCount - current.length;
            if (current.length === 0) setIsLoading(true);

            const load = async () => {
                setError(null);
                try {
                    if (current.length === 0 && shouldSyncWithServer()) {
                        await syncViewedProfiles(userId);
                    }
                    if (cancelled) return;

                    const incoming = await getFeaturedProfiles(userId, need);
                    if (cancelled) return;

                    const merged = mergeFeaturedProfiles(
                        profilesRef.current ?? [],
                        Array.isArray(incoming) ? incoming : [],
                    );
                    const previousIds = new Set(current.map((profile) => profile._id));
                    const addedIds = merged
                        .filter((profile) => !previousIds.has(profile._id))
                        .map((profile) => profile._id);
                    if (addedIds.length > 0) addMultipleViewedProfileIds(addedIds);

                    const next = merged.length > 0 ? merged : null;
                    profilesRef.current = next;
                    setFeaturedProfiles(next);
                } catch (loadError) {
                    if (!cancelled && (profilesRef.current?.length ?? 0) === 0) {
                        console.error('Error fetching featured profiles:', loadError);
                        setError('Failed to load featured profiles');
                    }
                } finally {
                    if (!cancelled) setIsLoading(false);
                }
            };

            void load();
        }, MEASURE_DEBOUNCE_MS);

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [userId, slotCount]);

    const loadedProfiles = featuredProfiles ?? [];
    const displayCount = visibleFeaturedCount(slotCount ?? MIN_FEATURED_SLOTS, loadedProfiles.length);
    const showSkeleton = isLoading && loadedProfiles.length === 0;
    const rowCount = showSkeleton ? (slotCount ?? MIN_FEATURED_SLOTS) : displayCount;
    const rowsFill = mode === 'fluid' && shouldFillFeaturedRows(listHeight, rowCount);
    // A remainder too small to buy another member stays centered. Once the
    // next member fits by condensing a little, the rows share the pane.
    const centerRows = mode === 'fluid' && !rowsFill;
    const avatarPx = rowsFill ? featuredAvatarPx(listHeight, rowCount) : FEATURED_AVATAR_PX;
    const rowClassName = rowsFill ? fillRowClassName : featuredRowClassName;
    const rowStyle = rowsFill ? undefined : featuredRowStyle;

    const renderSkeletonLoader = (count: number) => (
        <>
            {Array.from({ length: count }, (_, item) => (
                <div key={item} className={rowClassName} style={rowStyle}>
                    <div
                        className="skeleton-shimmer shrink-0 rounded-full"
                        style={{ width: avatarPx, height: avatarPx }}
                    />
                    <div className="ml-3 flex w-full min-w-0 flex-col justify-center">
                        <div className="skeleton-shimmer mb-2 h-4 w-3/4 rounded" />
                        <div className="skeleton-shimmer h-3 w-1/2 rounded" />
                    </div>
                </div>
            ))}
        </>
    );

    const renderEmptyPlaceholder = () => (
        <div className="flex h-full min-w-0 items-center">
            <div className="shrink-0 rounded-full bg-gray-100" style={{ width: avatarPx, height: avatarPx }} />
            <div className="ml-3 flex w-full min-w-0 flex-col justify-center font-sans">
                <span className="whitespace-normal text-base font-medium text-gray-400">
                    Get featured!
                </span>
                <span className="whitespace-normal text-sm font-medium text-gray-400">
                    Boosted/quality accounts get featured
                </span>
            </div>
        </div>
    );

    const renderError = () => (
        <div className="flex h-full flex-col items-center justify-center p-3">
            <div className="mb-2 text-red-500">Unable to load featured profiles</div>
            <div className="text-sm text-gray-500">Please try again later</div>
        </div>
    );

    const slots: (FeaturedProfile | null)[] = Array.from(
        { length: displayCount },
        (_, index) => loadedProfiles[index] ?? null
    );

    return (
        <div className="flex h-full min-h-0 w-full flex-col overflow-hidden rounded-lg border bg-white shadow-sm transition-shadow duration-300 hover:shadow-md">
            <div className="flex shrink-0 items-center justify-between border-b">
                <span className="flex h-10 items-center text-white">
                    <span className="featured-members-title flex h-10 items-center p-2 font-medium">
                        Featured Members
                    </span>
                </span>
                <span className="mr-4 text-xs text-gray-500">
                    Profiles you might like
                </span>
            </div>

            <div ref={listRef} className={`featured-members-list flex min-h-0 flex-1 flex-col ${rowsFill ? 'overflow-hidden' : 'overflow-y-auto'}`}>
                <div className={centerRows ? 'my-auto flex w-full flex-col' : rowsFill ? 'flex min-h-0 w-full flex-1 flex-col' : 'flex w-full flex-col'}>
                {showSkeleton ? renderSkeletonLoader(slotCount ?? MIN_FEATURED_SLOTS) : (
                    error ? renderError() : (
                        slots.map((profile, index) => {
                            if (!profile) {
                                return (
                                    <div key={`placeholder-${index}`} className={rowClassName} style={rowStyle}>
                                        {renderEmptyPlaceholder()}
                                    </div>
                                );
                            }

                            const displayName = getUserDisplayName({
                                displayName: profile.displayName,
                                firstName: profile.firstName,
                                lastName: profile.lastName,
                                username: profile.username
                            });

                            return (
                                <a
                                    key={profile._id}
                                    href={`/${profile.username}`}
                                    className={`${rowClassName} hover:bg-gray-50`}
                                    style={rowStyle}
                                >
                                    <div className="relative shrink-0" style={{ width: avatarPx, height: avatarPx }}>
                                        <AvatarImage
                                            src={profile.profilePhoto}
                                            wrapperClass="!h-full !w-full border-2 border-gray-100"
                                            isFeatured={true}
                                        />
                                        <div className="absolute -bottom-0.5 -right-0.5 rounded-full bg-white p-0.5">
                                            <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                                        </div>
                                    </div>
                                    <div className="ml-3 flex w-full min-w-0 flex-col justify-center font-sans">
                                        <span className="truncate text-base font-medium text-black" title={displayName}>
                                            {displayName}
                                        </span>
                                        <span className="truncate text-sm font-medium text-indigo-600" title={profile.headline ?? undefined}>
                                            {profile.headline}
                                        </span>
                                    </div>
                                </a>
                            );
                        })
                    )
                )}
                </div>
            </div>
        </div>
    );
};

export default FeaturedProfiles;
